import React, { useEffect, useState, useRef } from 'react';

interface AudioPermissionModalProps {
  isOpen: boolean;
  onAllow: () => void;
  onCancel: () => void;
}

const AudioPermissionModal: React.FC<AudioPermissionModalProps> = ({
  isOpen,
  onAllow,
  onCancel,
}) => {
  const [countdown, setCountdown] = useState(10);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onAllowRef = useRef(onAllow);

  // Keep the onAllow ref up to date
  useEffect(() => {
    onAllowRef.current = onAllow;
  }, [onAllow]);

  useEffect(() => {
    if (!isOpen) {
      setCountdown(10);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Start countdown from 10
    setCountdown(10);

    // Auto allow after 10 seconds
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        const newValue = prev - 1;
        console.log('Countdown:', newValue);
        if (newValue <= 0) {
          console.log('Countdown reached 0, auto-allowing');
          onCancel();
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          onAllowRef.current();

          return 0;
        }
        return newValue;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-modalBlur backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="flex flex-col items-center space-y-6">
          {/* Icon */}
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
            </svg>
          </div>

          {/* Message */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-dark mb-2">
              Audio Permission Required
            </h2>
            <p className="text-docDetail">
              Please allow audio playback to hear patient announcements
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 w-full">
            <button
              onClick={onAllow}
              className="flex-1 bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
              autoFocus
            >
              Allow {countdown > 0 && `(${countdown})`}
            </button>
            <button
              onClick={onCancel}
              className="flex-1 bg-closeBG text-signinText font-semibold py-3 px-6 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPermissionModal;

